import React, { useCallback, useEffect, useState } from 'react';
import { Card, Grid, Box, Switch, FormControlLabel, Typography, Tooltip, IconButton, Snackbar, Alert, Select, MenuItem, Divider, Backdrop, Stack, Icon } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { useDispatch, useSelector } from 'react-redux';
import { PmConfigType } from '../pmConfigType';
import SwitchConfig from './SwitchConfig';
import ConfirmRestoreDefaultsDialog from './confirmRestoreDefaultsDialog';
import { useHistory, useParams } from 'react-router';
import whiteBackIcon from '../../../icons/whiteBackIcon.svg';
import AmountInputConfig from './AmountInputConfig';
import { useStyles } from '../styles';
import { getAvailableBankAccount, selectBillingState } from '../../billingConfiguration/sagas/billingSlice';
import { resetDocumentsState } from '../../../shared/sagas/document/documentSlice';
import DeleteModal from './DeleteModal';
import debounce from 'lodash.debounce';
import ApplicationCards from './ApplicationCards';
import { AppConfigSettingsViewEnum } from '../applicationsConfigSettings';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import AddIcon from '../../../icons/addcard.png';
import EditIcon from '../../../icons/editIcon.svg';
import DeleteIcon from '../../../icons/deleteIcon.svg';
import { ApplicationsHoldDepoistPrTimeType } from '@inosio/payments-sdk';
import { clearPropertyGroupConfigSettingsFlags, getUpdatePropertyGroupConfigs, selectPropertyGroupDetailsState, selectSpecificPropertyGroupConfigs } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';

export interface ErrorObj {
  [key: string]: string;
}

interface PropsType {
  setView: React.Dispatch<React.SetStateAction<AppConfigSettingsViewEnum>>;
}

export default function ApplicationsDashboard({ setView }: PropsType) {
  const { uuid: propertyGroupIdParam } = useParams<{ uuid: string }>();
  const classes = useStyles();
  const history = useHistory();
  const [snackBarOpen, setSnackBarOpen] = React.useState(false);

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackBarOpen(false);
  };

  const dispatch = useDispatch();
  useEffect(() => {
    resetPage();
  }, []);

  const { selectedPropertyGroupAssignedConfigs, isFetchingAllPropertyGroupConfigsSuccess, UpdatingPropertyGroupConfigsError, isUpdatingPropertyGroupConfigsSuccess } = useSelector(selectPropertyGroupDetailsState);
  const { availableBankAccounts } = useSelector(selectBillingState);

  const [snakBarError, setSnakBarError] = React.useState('');
  useEffect(() => {
    if (UpdatingPropertyGroupConfigsError.message?.length > 0) {
      setSnackBarOpen(true);
      setSnakBarError(UpdatingPropertyGroupConfigsError.message);
    } else {
      setSnakBarError('');
    }
  }, [UpdatingPropertyGroupConfigsError]);

  const [pmConfig, setPmConfig] = useState<PmConfigType>({});

  useEffect(() => {
    dispatch({
      type: selectSpecificPropertyGroupConfigs.type,
      payload: {
        propertyGroupUuid: propertyGroupIdParam,
      },
    });
  }, [isFetchingAllPropertyGroupConfigsSuccess]);

  const resetPage = () => {
    dispatch(clearPropertyGroupConfigSettingsFlags({}));
    dispatch({ type: getAvailableBankAccount.type, payload: { fundableOnly: true } });
    dispatch(resetDocumentsState());
    setIsDeleteLegaleseModalOpen(false);
    setIsDeletePoliciesModalOpen(false);
    setIsDeletePetPoliciesModalOpen(false);
    setIsSpinnerModalOpen(false);
  };

  const [pendingConfig, setPendingConfig] = React.useState<any>(null);

  const handleChange = (ConfigTypeKey1: string, AssignedValue1: any, ConfigTypeKey2?: string, AssignedValue2?: any) => {
    let newPmConfig = {};

    if (ConfigTypeKey2 && AssignedValue2 !== undefined) {
      newPmConfig = {
        ...pmConfig,
        [ConfigTypeKey1]: AssignedValue1,
        [ConfigTypeKey2]: AssignedValue2,
      };
      setPendingConfig({
        ...pendingConfig,
        [ConfigTypeKey1]: AssignedValue1,
        [ConfigTypeKey2]: AssignedValue2,
      });
    } else {
      newPmConfig = {
        ...pmConfig,
        [ConfigTypeKey1]: AssignedValue1,
      };
      setPendingConfig({
        ...pendingConfig,
        [ConfigTypeKey1]: AssignedValue1,
      });
    }

    setPmConfig(newPmConfig);
  };

  const actuallyDispatchConfigUpdate = () => {
    if (pendingConfig) {
      dispatch(getUpdatePropertyGroupConfigs({ propertyGroupUuid: propertyGroupIdParam, productTypeId: 2, updatedPmConfig: pendingConfig }));
      setPendingConfig(null);
    }
  };

  const delayedConfigUpdate = useCallback(debounce(actuallyDispatchConfigUpdate, 750), [pendingConfig]);

  useEffect(() => {
    delayedConfigUpdate();
    return delayedConfigUpdate.cancel;
  }, [pendingConfig, delayedConfigUpdate]);

  const [errorObj, setErrorObj] = useState<ErrorObj>({});
  const handleError = (key: string, value: string) => setErrorObj({ ...errorObj, [key]: value });

  const confirmRestoreDefaults = () => {
    dispatch(getUpdatePropertyGroupConfigs({propertyGroupUuid: propertyGroupIdParam, productTypeId: 2, updatedPmConfig: {}, setToDefault: true }));
  };

  const [openConfirmRestoreDefaults, setOpenConfirmRestoreDefaults] = useState(false);
  const confirmConfig = {
    classes: classes,
    openConfirmRestoreDefaults: openConfirmRestoreDefaults,
    setOpenConfirmRestoreDefaults: setOpenConfirmRestoreDefaults,
    confirmRestoreDefaults: confirmRestoreDefaults,
  };

  const getTypography = (text: string, fontWeight: number, fontSize: string, restProps?: object) => (
    <Typography
      sx={{
        fontFamily: 'Work Sans',
        fontWeight: { fontWeight },
        fontSize: { fontSize },
        lineHeight: '22px',
        ...restProps,
      }}
    >
      {text}
    </Typography>
  );

  useEffect(() => {
    if (isUpdatingPropertyGroupConfigsSuccess) {
      dispatch(clearPropertyGroupConfigSettingsFlags({}));
      setIsDeleteLegaleseModalOpen(false);
      setIsDeletePoliciesModalOpen(false);
      setIsDeletePetPoliciesModalOpen(false);
      setIsSpinnerModalOpen(false);
    }
  }, [isUpdatingPropertyGroupConfigsSuccess]);

  useEffect(() => {
    setPmConfig(selectedPropertyGroupAssignedConfigs);
    setErrorObj({});
  }, [selectedPropertyGroupAssignedConfigs]);

  const [isDeleteLegaleseModalOpen, setIsDeleteLegaleseModalOpen] = React.useState(false);
  const handleConfirmDeleteLegaleseModal = () => {
    setIsSpinnerModalOpen(true);
    dispatch(
      getUpdatePropertyGroupConfigs({
        propertyGroupUuid: propertyGroupIdParam,
        productTypeId: 2,
        updatedPmConfig: {
          ApplicationsPublishedLegaleseText: '',
        },
      }),
    );
    setPmConfig({ ...pmConfig, ApplicationsPublishedLegaleseText: '' });
  };

  const [isDeletePoliciesModalOpen, setIsDeletePoliciesModalOpen] = React.useState(false);
  const handleConfirmDeletePoliciesModal = () => {
    setIsSpinnerModalOpen(true);
    dispatch(
      getUpdatePropertyGroupConfigs({
        propertyGroupUuid: propertyGroupIdParam,
        productTypeId: 2,
        updatedPmConfig: {
          ApplicationsPublishedPolicyText: '',
        },
      }),
    );
    setPmConfig({ ...pmConfig, ApplicationsPublishedPolicyText: '' });
  };

  const [isDeletePetPoliciesModalOpen, setIsDeletePetPoliciesModalOpen] = React.useState(false);
  const handleConfirmDeletePetPoliciesModal = () => {
    setIsSpinnerModalOpen(true);
    dispatch(
      getUpdatePropertyGroupConfigs({
        propertyGroupUuid: propertyGroupIdParam,
        productTypeId: 2,
        updatedPmConfig: {
          ApplicationsPublishedPetPolicyText: '',
        },
      }),
    );
    setPmConfig({ ...pmConfig, ApplicationsPublishedPetPolicyText: '' });
  };

  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const handleCloseSpinnerModal = () => {
    setIsSpinnerModalOpen(false);
  };

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card sx={{ display: 'flex', flexDirection: 'column', color: '#000' }}>
        <Box className={classes.headerContainer} sx={{ backgroundColor: '#BE6531' }}>
          <IconButton
            sx={{ marginRight: '10px', marginTop: '-4px' }}
            onClick={() => {
              resetPage();
              history.push(`/pmui/propertygroups/${propertyGroupIdParam}`);
            }}
          >
            <img src={whiteBackIcon} alt="" height="14px" />
          </IconButton>
          {pmConfig.ApplicationsProductPurchased && pmConfig.ApplicationsMasterFlag && <FormControlLabel sx={{ color: '#FFF', ml: 0 }} control={<Switch checked={!!pmConfig.ApplicationsProductEnabled} onChange={(event) => handleChange(event.target.name, event.target.checked)} name="ApplicationsProductEnabled" />} label={getTypography(selectedPropertyGroupAssignedConfigs.Name + ': Applications', 600, '24px')} labelPlacement="start" />}
        </Box>

        <Grid container sx={{ p: 2, pt: 2, pb: 1, position: 'relative' }}>
          <Grid item xs={12} md={12}>
            <ApplicationCards pmConfig={pmConfig} handleConfigChange={handleChange} setView={setView} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer} sx={{ justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getTypography('Application Policies', 600, '18px')}

                  <Tooltip title="Include your company process and policies that you want applicants to know PRIOR to submitting an application." placement="right">
                    <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                  </Tooltip>
                </Box>
              </Box>
              <Box className={classes.configBodyContainer}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                  {getTypography('Application Legalese', 400, '18px', { p: 2 })}
                  <Box sx={{ width: 60, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                      <IconButton
                        onClick={() => {
                          setView(AppConfigSettingsViewEnum.ApplicationsLegalese);
                        }}
                        sx={{ p: 0.25, mr: '-2px' }}
                      >
                        <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {pmConfig.ApplicationsPublishedLegaleseText ? (
                            <img
                              style={{
                                cursor: 'pointer',
                                width: '14px',
                                height: '14px',
                              }}
                              src={EditIcon}
                              alt="Edit"
                            />
                          ) : (
                            <img
                              style={{
                                cursor: 'pointer',
                                width: 20,
                                height: 20,
                              }}
                              src={AddIcon}
                              alt="Add an item"
                            />
                          )}
                        </Icon>
                      </IconButton>
                    </Stack>
                    {pmConfig.ApplicationsPublishedLegaleseText && (
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <IconButton
                          onClick={() => {
                            setIsDeleteLegaleseModalOpen(true);
                          }}
                          sx={{ p: 0.25 }}
                        >
                          <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              style={{
                                cursor: 'pointer',
                                width: '15px',
                                height: '18px',
                              }}
                              src={DeleteIcon}
                              alt="Delete"
                            />
                          </Icon>
                        </IconButton>
                      </Stack>
                    )}
                  </Box>
                </Box>
                <Divider style={{ border: '0.5px solid #F1F0F5' }} sx={{ mx: -2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                  {getTypography('Application Policies', 400, '18px', { p: 2 })}
                  <Box sx={{ width: 60, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                      <IconButton
                        onClick={() => {
                          setView(AppConfigSettingsViewEnum.ApplicationsPolicies);
                        }}
                        sx={{ p: 0.25, mr: '-2px' }}
                      >
                        <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {pmConfig.ApplicationsPublishedPolicyText ? (
                            <img
                              style={{
                                cursor: 'pointer',
                                width: '14px',
                                height: '14px',
                              }}
                              src={EditIcon}
                              alt="Edit"
                            />
                          ) : (
                            <img
                              style={{
                                cursor: 'pointer',
                                width: 20,
                                height: 20,
                              }}
                              src={AddIcon}
                              alt="Add an item"
                            />
                          )}
                        </Icon>
                      </IconButton>
                    </Stack>
                    {pmConfig.ApplicationsPublishedPolicyText && (
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <IconButton
                          onClick={() => {
                            setIsDeletePoliciesModalOpen(true);
                          }}
                          sx={{ p: 0.25 }}
                        >
                          <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              style={{
                                cursor: 'pointer',
                                width: '15px',
                                height: '18px',
                              }}
                              src={DeleteIcon}
                              alt="Delete"
                            />
                          </Icon>
                        </IconButton>
                      </Stack>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>
                {getTypography('Hold Deposit', 600, '18px')}

                <Tooltip title="Money that are collected from applicants prior to lease signing." placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                </Tooltip>
              </Box>
              <Box className={classes.configBodyContainer} sx={{ minHeight: 165 }}>
                <Box sx={{ mt: 2 }}>
                  {getTypography('Applicants pay a', 400, '18px', { display: 'inline-block' })}
                  <Select value={!!pmConfig.ApplicationsHoldDepositIsRefundable ? 'refundable' : 'non-refundable'} displayEmpty sx={{ borderRadius: '24px', height: '31px', mx: 2, my: 1, '.MuiTypography-root': { fontSize: '14px' } }} onChange={(e) => handleChange('ApplicationsHoldDepositIsRefundable', e.target.value === 'refundable')}>
                    <MenuItem value="non-refundable">non-refundable</MenuItem>
                    <MenuItem value="refundable">refundable</MenuItem>
                  </Select>
                  {getTypography('hold deposit when they', 400, '18px', { display: 'inline-block' })}
                  <Select value={pmConfig.ApplicationsHoldDepositPrTime ?? ''} displayEmpty sx={{ borderRadius: '24px', height: '31px', mx: 2, my: 1, '.MuiTypography-root': { fontSize: '14px' } }} onChange={(e) => handleChange('ApplicationsHoldDepositPrTime', e.target.value)}>
                    <MenuItem disabled value={''}>
                      <Typography>Select one</Typography>
                    </MenuItem>
                    {Object.values(ApplicationsHoldDepoistPrTimeType).map((type, index) => (
                      <MenuItem value={type} key={index}>
                        {type === 'Approval' ? 'have been approved' : type === 'Submission' ? 'have submitted the application' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.7 }}>
                  {getTypography('Hold Deposit Amount:', 400, '18px')}
                  <Box sx={{ width: 100, mx: 2, my: -1 }}>
                    <AmountInputConfig name="ApplicationsHoldDepositAmount" value={pmConfig.ApplicationsHoldDepositAmount} onChange={handleChange} error={''} onError={handleError} />
                  </Box>
                </Box>
                {errorObj.ApplicationsHoldDepositAmount && (
                  <Typography
                    sx={{
                      mt: '-24px',
                      mb: 1,
                      mr: 2,
                      textAlign: 'right',
                    }}
                    className={classes.errorText}
                  >
                    {errorObj.ApplicationsHoldDepositAmount}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.3, mb: 1.5 }}>
                  {getTypography('Bank Account:', 400, '18px')}
                  {!!availableBankAccounts?.length && (
                    <Select value={pmConfig.ApplicationsHoldDepositBankAccountUuid ?? ''} displayEmpty sx={{ borderRadius: '24px', height: '31px', mx: 2, my: 1, '.MuiTypography-root': { fontSize: '14px' } }} onChange={(e) => handleChange('ApplicationsHoldDepositBankAccountUuid', e.target.value)}>
                      <MenuItem disabled value={''}>
                        <Typography>Select account</Typography>
                      </MenuItem>
                      {availableBankAccounts.map((bankAccount) => (
                        <MenuItem value={bankAccount.bankAccountUuid} key={bankAccount.bankAccountUuid} selected={bankAccount.bankAccountUuid === pmConfig.ApplicationsHoldDepositBankAccountUuid}>
                          <Typography>****{bankAccount.last4}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Box>
                {errorObj.ApplicationsHoldDepositBankAccountUuid && (
                  <Typography
                    sx={{
                      mt: '-16px',
                      mb: 1,
                      mr: 2,
                      textAlign: 'right',
                    }}
                    className={classes.errorText}
                  >
                    {errorObj.ApplicationsHoldDepositBankAccountUuid}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>
                {getTypography('Payment Details', 600, '18px')}

                <Tooltip title="Which bank account should we deposit application fee funds to? How much do you charge to process a single adult application?" placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                </Tooltip>
              </Box>
              <Box className={classes.configBodyContainer}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                  {getTypography('How much do you charge per applicant/co-applicant?', 400, '18px', { p: 2 })}
                  <Box sx={{ width: 100, mx: 2, my: 0.7 }}>
                    <AmountInputConfig name="ApplicationsApplicantFee" value={pmConfig.ApplicationsApplicantFee} onChange={handleChange} error={''} onError={handleError} />
                  </Box>
                </Box>
                {errorObj.ApplicationsApplicantFee && (
                  <Typography
                    sx={{
                      mt: '-24px',
                      mb: 1,
                      mr: 2,
                      textAlign: 'right',
                    }}
                    className={classes.errorText}
                  >
                    {errorObj.ApplicationsApplicantFee}
                  </Typography>
                )}
                <Divider style={{ border: '0.5px solid #F1F0F5' }} sx={{ mx: -2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                  {getTypography('Deposit Account:', 400, '18px', { p: 2 })}
                  {!!availableBankAccounts?.length && (
                    <Select value={pmConfig.ApplicationsDepositBankAccountUuid ?? ''} displayEmpty sx={{ borderRadius: '24px', height: '31px', m: 2, '.MuiTypography-root': { fontSize: '14px' } }} onChange={(e) => handleChange('ApplicationsDepositBankAccountUuid', e.target.value)}>
                      <MenuItem disabled value={''}>
                        <Typography>Select account</Typography>
                      </MenuItem>
                      {availableBankAccounts.map((bankAccount) => (
                        <MenuItem value={bankAccount.bankAccountUuid} key={bankAccount.bankAccountUuid} selected={bankAccount.bankAccountUuid === pmConfig.ApplicationsDepositBankAccountUuid}>
                          <Typography>****{bankAccount.last4}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Box>
                {errorObj.ApplicationsDepositBankAccountUuid && (
                  <Typography
                    sx={{
                      mt: '-16px',
                      mb: 1,
                      mr: 2,
                      textAlign: 'right',
                    }}
                    className={classes.errorText}
                  >
                    {errorObj.ApplicationsDepositBankAccountUuid}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>
                {getTypography('Accepted Payment Types', 600, '18px')}

                <Tooltip title="What payment methods can be processed for application payments?" placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                </Tooltip>
              </Box>
              <Box className={classes.configBodyContainer}>
                <SwitchConfig name="ApplicationsAchAccepted" label="ACH payments" checked={!!pmConfig.ApplicationsAchAccepted} onChange={handleChange} showLabel={true} />
                <SwitchConfig name="ApplicationsCcAccepted" label="Credit card payments" checked={!!pmConfig.ApplicationsCcAccepted} onChange={handleChange} showLabel={true} />
                <SwitchConfig name="ApplicationsDbAccepted" label="Debit card payments" checked={!!pmConfig.ApplicationsDbAccepted} onChange={handleChange} showLabel={true} />
                {errorObj.PaymentMethods && (
                  <Typography
                    sx={{
                      mt: '-8px',
                      mb: 1,
                      ml: 2,
                      textAlign: 'left',
                    }}
                    className={classes.errorText}
                  >
                    {errorObj.PaymentMethods}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer} sx={{ justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getTypography('Animal Policies', 600, '18px')}

                  <Tooltip title="Do you allow Applicants with animals?" placement="right">
                    <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                  </Tooltip>
                </Box>

                {!pmConfig.ApplicationsPublishedPetPolicyText && (
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => {
                        setView(AppConfigSettingsViewEnum.ApplicationsPetPolicies);
                      }}
                      sx={{ p: 0.8, mr: '-8px' }}
                    >
                      <Icon sx={{ width: 26, height: 26 }}>
                        <img
                          style={{
                            cursor: 'pointer',
                            width: 26,
                            height: 26,
                          }}
                          src={AddIcon}
                          alt="Add an item"
                        />
                      </Icon>
                    </IconButton>
                  </Stack>
                )}
              </Box>
              <Box className={classes.configBodyContainer}>
                {pmConfig.ApplicationsPublishedPetPolicyText && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
                    <div
                      style={{
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '18px',
                        color: '#14142B',
                        width: 'calc( 100% - 80px )',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                      }}
                      dangerouslySetInnerHTML={{ __html: pmConfig.ApplicationsPublishedPetPolicyText ?? '' }}
                    />
                    <Box sx={{ width: 60, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <IconButton
                          onClick={() => {
                            setView(AppConfigSettingsViewEnum.ApplicationsPetPolicies);
                          }}
                          sx={{ p: 0.25, mr: '-2px' }}
                        >
                          <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              style={{
                                cursor: 'pointer',
                                width: '14px',
                                height: '14px',
                              }}
                              src={EditIcon}
                              alt="Edit"
                            />
                          </Icon>
                        </IconButton>
                      </Stack>
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <IconButton
                          onClick={() => {
                            setIsDeletePetPoliciesModalOpen(true);
                          }}
                          sx={{ p: 0.25 }}
                        >
                          <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              style={{
                                cursor: 'pointer',
                                width: '15px',
                                height: '18px',
                              }}
                              src={DeleteIcon}
                              alt="Delete"
                            />
                          </Icon>
                        </IconButton>
                      </Stack>
                    </Box>
                  </Box>
                )}

                <SwitchConfig name="ApplicationsAllowPets" label="Allow animals" checked={!!pmConfig.ApplicationsAllowPets} onChange={handleChange} showLabel={true} />

                <AmountInputConfig name="ApplicationsAppPetFee" label="Per Animal Fee" value={pmConfig.ApplicationsAppPetFee} onChange={handleChange} error={''} onError={handleError} />
                {errorObj.ApplicationsAppPetFee && (
                  <Typography
                    sx={{
                      mt: '-8px',
                      mb: 1,
                      ml: 2,
                      textAlign: 'left',
                    }}
                    className={classes.errorText}
                  >
                    {errorObj.ApplicationsAppPetFee}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>
                {getTypography('Applicant Communication', 600, '18px')}

                <Tooltip title="Do you allow to send approval/denial notification to applicants?" placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: "#C4C4C4", cursor: "pointer", ml: '10px' }} />
                </Tooltip>
              </Box>
              <Box className={classes.configBodyContainer}>
                <SwitchConfig
                  name="ShowApplicationDecisionNotificationOption"
                  label="Send approval/denial notification to applicants?"
                  checked={!!pmConfig.ShowApplicationDecisionNotificationOption}
                  onChange={handleChange}
                  showLabel={true}
                />
                {errorObj.ShowApplicationDecisionNotificationOption && (
                  <Typography 
                    sx={{
                      mt: '-8px',
                      mb: 1, 
                      ml: 2,
                      textAlign: 'left',
                    }} 
                    className={classes.errorText}
                  >
                    {errorObj.ShowApplicationDecisionNotificationOption}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Backdrop sx={{ color: '#fff', zIndex: 30, position: 'absolute', backgroundColor: 'rgb(255, 255, 255, 0.5)' }} open={!pmConfig.ApplicationsProductEnabled || !pmConfig.ApplicationsProductPurchased || !pmConfig.ApplicationsMasterFlag}></Backdrop>
        </Grid>

        <Box id="dialogs">
          <ConfirmRestoreDefaultsDialog {...confirmConfig} />
        </Box>
      </Card>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {snakBarError}
        </Alert>
      </Snackbar>

      {isDeletePoliciesModalOpen && <DeleteModal open={isDeletePoliciesModalOpen} confirmCallback={handleConfirmDeletePoliciesModal} closeCallback={() => setIsDeletePoliciesModalOpen(false)} />}
      {isDeletePetPoliciesModalOpen && <DeleteModal open={isDeletePetPoliciesModalOpen} confirmCallback={handleConfirmDeletePetPoliciesModal} closeCallback={() => setIsDeletePetPoliciesModalOpen(false)} />}
      {isDeleteLegaleseModalOpen && <DeleteModal open={isDeleteLegaleseModalOpen} confirmCallback={handleConfirmDeleteLegaleseModal} closeCallback={() => setIsDeleteLegaleseModalOpen(false)} />}
      {isSpinnerModalOpen && (
        <SpinnerModal
          width="472px"
          height="523px"
          isInvisible={true}
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}
    </Grid>
  );
}
